/**
 * @copyright
 * Copyright (c) 2022 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import ITheme from "@atg/icf-react-components/themes/Theme";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { createUseStyles } from "react-jss";

import cbLogoGraySvg from "~/assets/images/cb_logo_gray_01.svg";
import CustomLink from "~/components/CustomLink";
import { JSS_MEDIA_MOBILE_MAX, PAGE_CONTENT_MAX_WIDTH, PAGE_CONTENT_PADDING } from "~/constants";
import LangSelector from "~/features/footer/components/LangSelector";
import customColors from "~/utils/customColors";

const useStyle = createUseStyles((theme: ITheme) => ({
  footer: {
    backgroundColor: customColors.lightBlack, // TODO color map
    fontWeight: 300,
    color: theme.colors.baseContrast040,
  },
  footerInner: {
    boxSizing: "border-box",
    width: PAGE_CONTENT_MAX_WIDTH, // TODO container?
    maxWidth: "100%",
    margin: "0 auto",
    padding: `0 ${PAGE_CONTENT_PADDING}px`,
  },
  top: {
    padding: "31px 0 40px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    maxWidth: "100%",
  },
  logoWrapper: {
    flex: "1 1 322px",
    maxWidth: "322px",
  },
  logo: {
    display: "block",
    width: "100%",
    // eslint-disable-next-line @atg/css-rtl-support
    marginLeft: "-7px",
  },
  compliance: {
    marginBottom: "39px",
    "& p": {
      margin: 0,
    },
  },
  about: {
    marginBottom: "30px",
  },
  bottom: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderTop: `1px solid ${theme.colors.baseContrast010}`,
    padding: "8px 0 33px",
    "& p": {
      margin: 0,
      "&$bottomLinks": {
        [JSS_MEDIA_MOBILE_MAX]: {
          marginTop: "10px",
        },
      },
    },
    [JSS_MEDIA_MOBILE_MAX]: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
  bottomLinks: {
    display: "grid",
    gridAutoColumns: "max-content",
    gridAutoFlow: "column",
    gridColumnGap: "14px",
  },
  link: {
    fontWeight: 300,
    color: theme.colors.baseContrast060,
    transition: "color 0.2s ease-in-out",
    "&:hover": {
      color: theme.colors.baseContrast100,
    },
  },
}));

const icfLegalUrl = "https://icflegal.com/cambuilder";

const Footer: FC = () => {
  const classes = useStyle();

  // TODO replace with the code under if needed in the future;
  // const { showHealthWarning } = getServerContext() ?? false
  const showHealthWarning = false; // Turned off because DEV-96931

  const { t } = useTranslation("footer");

  return (
    <footer className={classes.footer}>
      <div className={classes.footerInner}>
        <section className={classes.top}>
          <a href="/" className={classes.logoWrapper}>
            <img className={classes.logo} src={cbLogoGraySvg} alt="Cambuilder Logo" />
          </a>

          <LangSelector />
        </section>

        <section className={classes.compliance}>
          <CustomLink className={classes.link} to="/app/compliance">
            {t("compliance")}
          </CustomLink>
          <p>{t("models_age")}</p>
        </section>

        <section className={classes.about}>
          {t("about")}:{" "}
          <CustomLink className={classes.link} to="/app/learn-more/network">
            {t("about_links.network")}
          </CustomLink>{" "}
          |{" "}
          <CustomLink className={classes.link} to="/app/learn-more/webcams">
            {t("about_links.webcams")}
          </CustomLink>{" "}
          |{" "}
          <CustomLink className={classes.link} to="/app/learn-more/affiliates">
            {t("about_links.affiliates")}
          </CustomLink>{" "}
          |{" "}
          <CustomLink className={classes.link} to="/app/learn-more/product">
            {t("about_links.product")}
          </CustomLink>{" "}
          |{" "}
          <CustomLink className={classes.link} to="/app/learn-more/customizations">
            {t("about_links.customizations")}
          </CustomLink>
        </section>

        <section className={classes.bottom}>
          <p>
            ©{new Date().getFullYear()} {t("copy")}
          </p>
          <p className={classes.bottomLinks}>
            <CustomLink className={classes.link} to="/app/support/faq">
              {t("bottom_links.support")}
            </CustomLink>
            <a className={classes.link} target="_blank" rel="noreferrer" href={icfLegalUrl}>
              {t("bottom_links.legal")}
            </a>

            {showHealthWarning ? (
              <a className={classes.link} href="https://cdn.icflegal.com/" target="_blank" rel="noreferrer">
                {t("bottom_links.health_warning")}
              </a>
            ) : (
              ""
            )}

            <CustomLink className={classes.link} to="/app/support/contact">
              {t("bottom_links.contact")}
            </CustomLink>
          </p>
        </section>
      </div>
    </footer>
  );
};

export default Footer;
